import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useCounty } from "../lib/CountyContext";
import { CaretDown, MapPinLine, Check } from "@phosphor-icons/react";

const MENU_W = 224; // matches w-56

export default function CountySelector() {
  const { county, setCounty, counties, current } = useCounty();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const loc = useLocation();

  // Position the portal menu just below the trigger.
  const updatePos = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuW = Math.min(MENU_W, window.innerWidth - 16);
    // right-anchor to the button, but clamp so the menu never runs off the left edge
    const right = Math.min(
      Math.max(8, window.innerWidth - r.right),
      Math.max(8, window.innerWidth - menuW - 8),
    );
    setPos({
      top: r.bottom + 6, // 6px gap
      right,
      width: menuW,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePos();
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePos();
    const onClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, updatePos]);

  const menu = open && (
    <div
      ref={menuRef}
      data-testid="county-selector-menu"
      style={{
        position: "fixed",
        top: pos.top,
        right: pos.right,
        width: pos.width || MENU_W,
        zIndex: 99999,
      }}
      className="bg-tac-bg border border-tac-border rounded-sm shadow-2xl max-h-96 overflow-y-auto tac-scroll"
    >
      <div className="px-3 py-2 border-b border-tac-border text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
        Select County
      </div>
      {counties.filter((c) => !["washington", "bcborder"].includes(c.slug)).map((c) => {
        const active = c.slug === county;
        return (
          <button
            key={c.slug}
            data-testid={`county-option-${c.slug}`}
            onClick={() => {
              setCounty(c.slug);
              setOpen(false);
              if (loc.pathname.startsWith("/region/")) navigate("/");
            }}
            className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-tac-surface2 transition-colors ${active ? "bg-tac-surface text-tac-primary" : "text-tac-text"}`}
          >
            <span className="font-medium">{c.name}</span>
            {active ? (
              <Check size={12} weight="bold" />
            ) : (
              <span className="text-[15px] font-mono-tac text-tac-faint ml-2">{c.fips}</span>
            )}
          </button>
        );
      })}
      {counties.length === 0 && (
        <div className="px-3 py-4 text-[15px] text-tac-faint text-center">Loading…</div>
      )}
    </div>
  );

  return (
    <div className="relative" data-testid="county-selector">
      <button
        ref={btnRef}
        data-testid="county-selector-button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 h-9 text-xs font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-borderBright text-tac-text rounded-sm transition-colors"
      >
        <MapPinLine size={14} weight="fill" className="text-tac-primary" />
        <span className="hidden sm:inline">{current.name}{["washington", "bcborder"].includes(current.slug) ? "" : " Co."}</span>
        <span className="sm:hidden">{current.name}</span>
        <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {typeof document !== "undefined" && menu ? createPortal(menu, document.body) : null}
    </div>
  );
}
